import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, withModifiers as _withModifiers, withCtx as _withCtx, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

_pushScopeId("data-v-3edbb3a2")
const _hoisted_1 = { class: "explanation" }
_popScopeId()

import { computed, inject, reactive } from "vue";
import type { PropType } from "vue";
import { useI18n } from "vue-i18n";
import { callCreateEntitlementTopOff } from "@/api/licensing";
import type { Toasts, UsageCounter, UsageTopOffCreationRequest } from "@/types";


export default /*#__PURE__*/_defineComponent({
  props: {
  isOpen: {
    type: Boolean,
    required: true
  },
  entitlementId: {
    type: String,
    required: true
  },
  counter: {
    type: Object as PropType<UsageCounter>,
    required: true
  }
},
  emits: ["cancel", "granted"],
  setup(__props, { emit }) {

const props = __props




const { t } = useI18n();
const toasts = inject<Toasts | undefined>("toasts");
const handleError = inject<((error: unknown) => void) | undefined>("handleError");

const state = reactive({
  isOpen: computed(() => props.isOpen),
  allowance: null as number | null,
  expiresAt: null as Date | null,
  orderId: "",
  notes: "",
  loading: false
});

const counterLabel = computed(
  () => props.counter.name ?? t("entitlements.counters.default")
);

const allowanceHint = computed(() =>
  props.counter.unit
    ? t("entitlements.counters.topOffModal.allowanceHint", {
        unit: props.counter.unit
      })
    : undefined
);

const validateAllowance = (value: unknown) =>
  (Number(value) > 0 && Number.isFinite(Number(value))) ||
  t("validation.positiveNumber");

const isFormValid = computed(() => {
  const allowance = Number(state.allowance);
  return Number.isFinite(allowance) && allowance > 0;
});

const buildPayload = (): UsageTopOffCreationRequest => ({
  // A null counter designates the default counter of the entitlement.
  counter: props.counter.isDefault ? null : props.counter.name ?? null,
  allowance: Number(state.allowance),
  expiresAt: state.expiresAt ? state.expiresAt.toISOString() : null,
  orderId: state.orderId.trim() || null,
  notes: state.notes.trim() || null
});

const handleSubmit = async () => {
  if (!isFormValid.value || state.loading) {
    return;
  }
  state.loading = true;
  try {
    await callCreateEntitlementTopOff(props.entitlementId, buildPayload());
    toasts?.info(t("entitlements.counters.topOffModal.success"));
    emit("granted");
  } catch (error: any) {
    handleError?.(error?.response?.data ?? error);
    toasts?.error(t("entitlements.counters.topOffModal.error"));
  } finally {
    state.loading = false;
  }
};

return (_ctx: any,_cache: any) => {
  const _component_va_input = _resolveComponent("va-input")!
  const _component_va_date_input = _resolveComponent("va-date-input")!
  const _component_va_form = _resolveComponent("va-form")!
  const _component_va_button = _resolveComponent("va-button")!
  const _component_va_modal = _resolveComponent("va-modal")!

  return (_openBlock(), _createBlock(_component_va_modal, {
    modelValue: _unref(state).isOpen,
    "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => (_unref(state).isOpen = $event)),
    "hide-default-actions": "",
    title: _ctx.$t('entitlements.counters.topOffModal.title', { counter: _unref(counterLabel) })
  }, {
    footer: _withCtx(() => [
      _createVNode(_component_va_button, {
        flat: "",
        outline: "",
        class: "mr-1",
        disabled: _unref(state).loading,
        onClick: _cache[4] || (_cache[4] = ($event: any) => (emit('cancel')))
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t("buttons.cancel")), 1)
        ]),
        _: 1
      }, 8, ["disabled"]),
      _createVNode(_component_va_button, {
        class: "ml-1",
        color: "success",
        loading: _unref(state).loading,
        disabled: !_unref(isFormValid),
        onClick: handleSubmit
      }, {
        default: _withCtx(() => [
          _createTextVNode(_toDisplayString(_ctx.$t("entitlements.counters.topOffModal.submit")), 1)
        ]),
        _: 1
      }, 8, ["loading", "disabled"])
    ]),
    default: _withCtx(() => [
      _createElementVNode("p", _hoisted_1, _toDisplayString(_ctx.$t("entitlements.counters.topOffModal.explanation")), 1),
      _createVNode(_component_va_form, {
        ref: (_value, _refs) => {
          _refs['form'] = _value
        },
        onSubmit: _withModifiers(handleSubmit, ["prevent"])
      }, {
        default: _withCtx(() => [
          _createVNode(_component_va_input, {
            modelValue: _unref(state).allowance,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_unref(state).allowance = $event)),
            modelModifiers: { number: true },
            type: "number",
            min: "0",
            class: "control-margin",
            label: _ctx.$t('entitlements.counters.topOffModal.allowance'),
            hint: _unref(allowanceHint),
            rules: [validateAllowance],
            disabled: _unref(state).loading
          }, null, 8, ["modelValue", "label", "hint", "rules", "disabled"]),
          _createVNode(_component_va_date_input, {
            modelValue: _unref(state).expiresAt,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => (_unref(state).expiresAt = $event)),
            clearable: "",
            class: "control-margin",
            label: _ctx.$t('entitlements.counters.topOffModal.expiresAt'),
            placeholder: _ctx.$t('entitlements.counters.topOffModal.expiresAtPlaceholder'),
            disabled: _unref(state).loading
          }, null, 8, ["modelValue", "label", "placeholder", "disabled"]),
          _createVNode(_component_va_input, {
            modelValue: _unref(state).orderId,
            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => (_unref(state).orderId = $event)),
            class: "control-margin",
            label: _ctx.$t('entitlements.counters.topOffModal.orderId'),
            disabled: _unref(state).loading
          }, null, 8, ["modelValue", "label", "disabled"]),
          _createVNode(_component_va_input, {
            modelValue: _unref(state).notes,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => (_unref(state).notes = $event)),
            type: "textarea",
            class: "control-margin",
            label: _ctx.$t('entitlements.counters.topOffModal.notes'),
            disabled: _unref(state).loading
          }, null, 8, ["modelValue", "label", "disabled"])
        ]),
        _: 1
      }, 8, ["onSubmit"])
    ]),
    _: 1
  }, 8, ["modelValue", "title"]))
}
}

})